import {
  FormSubmitButton,
  FormRadioGroup,
  FormProvider,
  useForm,
} from '@mfw/forms';
import { RadioField, useIntl, Button } from '@mfw/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';

import type { UiUserTrainingLevelStep } from '../../../../../schemas/users.schema';
import type { ClientOnboardingStepsType } from '../../clientOnboarding.steps';

import { UiUserTrainingLevelStepSchema } from '../../../../../schemas/users.schema';
import { useUpdateUser } from '../../../../../hooks/usersv2';
import OnboardingHeader from '../../../OnboardingHeader';
import { useAuthContext } from '../../../../../context';
import { useWizard } from '../../../../../allFeatures';
import generalMessages from '../../../../../messages';
import messages from '../TrainingLevelStep/messages';

export const TrainingLevelDialogStep = () => {
  const { formatMessage } = useIntl();
  const { goToStep } = useWizard<ClientOnboardingStepsType>();
  const { mutateAsync: updateUserAsync } = useUpdateUser();
  const { currentUser } = useAuthContext();

  const methods = useForm({
    defaultValues: {
      fitnessLevel: currentUser?.fitnessProfile?.fitnessLevel?.toString() ?? '',
    },
    resolver: zodResolver(UiUserTrainingLevelStepSchema),
  });

  const handleNext = async (data: UiUserTrainingLevelStep) => {
    await updateUserAsync({
      fitnessProfile: {
        fitnessLevel: data.fitnessLevel,
      },
    });
    goToStep('fitnessGoals');
  };

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          position: 'sticky',
          px: 3,
          py: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <OnboardingHeader
          description={<p>{formatMessage(messages.description)}</p>}
          title={formatMessage(messages.title)}
        />
      </Box>
      <Box px={3} py={2}>
        <FormRadioGroup
          data-testid="client-onboarding-fitness-level"
          name="fitnessLevel"
        >
          <RadioField
            helperText={formatMessage(messages.notFitDescription)}
            label={formatMessage(messages.notFit)}
            value="1"
          />
          <RadioField
            helperText={formatMessage(messages.almostFitDescription)}
            label={formatMessage(messages.almostFit)}
            value="2"
          />
          <RadioField
            helperText={formatMessage(messages.fitDescription)}
            label={formatMessage(messages.fit)}
            value="3"
          />
          <RadioField
            helperText={formatMessage(messages.proFitDescription)}
            label={formatMessage(messages.proFit)}
            value="4"
          />
          <RadioField
            helperText={formatMessage(messages.advancedFitDescription)}
            label={formatMessage(messages.advancedFit)}
            value="5"
          />
        </FormRadioGroup>
      </Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          position: 'sticky',
          px: 2,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Button
          onClick={() => goToStep('about')}
          data-testid="onboarding-back"
          variant="outlined"
          size="large"
        >
          {formatMessage(generalMessages.back)}
        </Button>
        <FormSubmitButton
          data-testid="onboarding-next"
          variant="contained"
          disableIfNotDirty
          size="large"
        >
          {formatMessage(generalMessages.next)}
        </FormSubmitButton>
      </Box>
    </FormProvider>
  );
};
